/**
 * Graph API client for SharePoint list operations.
 *
 * Pure service layer — no React imports.
 * Uses @microsoft/microsoft-graph-client with token injection from MSAL.
 * Manually follows @odata.nextLink for pagination (not PageIterator).
 */

import { Client } from '@microsoft/microsoft-graph-client';
import type { SharePointListItem } from '../types/sharepoint';
import { SHAREPOINT_SITE_ID, LIST_ID_BY_NAME } from '../config/sharepoint';
import { GraphApiError } from './graphError';
import { isApiClient, type ApiClient } from './apiClient';

// Re-export for existing importers (e.g. documentLibraryService).
export { GraphApiError };

// AP (B2B guest) flows pass an ApiClient instead of a Graph SDK Client; the
// generic CRUD functions below branch to the backend API for those. The client
// is typed as `Client` everywhere (a cast in useGraphClient) so feature
// services need no changes — this narrows it back when it's actually an API client.
function asApi(client: Client): ApiClient | null {
  return isApiClient(client as unknown) ? (client as unknown as ApiClient) : null;
}

// ---------------------------------------------------------------------------
// Error handling helper
// ---------------------------------------------------------------------------

function handleGraphError(error: unknown, context: string): never {
  // Graph SDK wraps errors with statusCode and body
  const err = error as {
    statusCode?: number;
    body?: string;
    message?: string;
    headers?: Record<string, string>;
  };

  const statusCode = err.statusCode ?? 500;
  const requestId =
    (err.headers?.['request-id'] as string | undefined) ?? '';

  // Parse body for detailed message
  let detail = err.message ?? 'Unknown Graph API error';
  if (err.body) {
    try {
      const body = JSON.parse(err.body) as {
        error?: { message?: string };
      };
      if (body.error?.message) {
        detail = body.error.message;
      }
    } catch {
      // body is not JSON, use as-is
      detail = err.body;
    }
  }

  if (statusCode === 401 || statusCode === 403) {
    throw new GraphApiError(
      statusCode,
      `${context}: Token expired or insufficient permissions — ${detail}`,
      requestId,
    );
  }

  if (statusCode === 404) {
    throw new GraphApiError(
      statusCode,
      `${context}: Not found — ${detail}`,
      requestId,
    );
  }

  if (statusCode === 429) {
    const retryAfter = err.headers?.['retry-after'] ?? 'unknown';
    console.warn(
      `[GraphApiError] 429 Too Many Requests (retry-after: ${retryAfter})`,
    );
    throw new GraphApiError(
      429,
      `${context}: Rate limited — retry after ${retryAfter}s`,
      requestId,
    );
  }

  throw new GraphApiError(statusCode, `${context}: ${detail}`, requestId);
}

// ---------------------------------------------------------------------------
// Client factory
// ---------------------------------------------------------------------------

/**
 * Create a Graph API client with token injection.
 *
 * @param getToken - Async function that returns a valid access token
 *                   (typically useAuth().getGraphToken bound to a ref)
 */
export function createGraphClient(
  getToken: () => Promise<string>,
): Client {
  return Client.init({
    authProvider: (done) => {
      getToken()
        .then((token) => done(null, token))
        .catch((err: unknown) =>
          done(err, null),
        );
    },
  });
}

// ---------------------------------------------------------------------------
// Site and list resolution (hardcoded — see config/sharepoint.ts)
//
// AP guests have only Limited Access at the web, so they cannot resolve the
// site object or enumerate/filter lists by name (those return 403). We return
// the stable, pre-gathered IDs instead and address the granted lists directly,
// which the guest's per-list grants allow.
// ---------------------------------------------------------------------------

/** Return the hardcoded SharePoint site ID (no Graph call). */
export function getSiteId(): string {
  return SHAREPOINT_SITE_ID;
}

/**
 * Return the hardcoded list GUID for a list display name (no Graph call).
 * Throws a 404 GraphApiError for an unknown list name.
 */
export function getListId(listName: string): string {
  const listId = LIST_ID_BY_NAME[listName];
  if (!listId) {
    throw new GraphApiError(404, `getListId(${listName}): unknown list name`);
  }
  return listId;
}

// ---------------------------------------------------------------------------
// CRUD operations
// ---------------------------------------------------------------------------

/** Options for listItems query */
export interface ListItemsOptions {
  filter?: string;
  orderby?: string;
  top?: number;
  select?: string[];
}

/** Result of a listItems call */
export interface ListItemsResult<T> {
  items: T[];
  nextLink?: string;
}

/**
 * Fetch list items with automatic pagination.
 * Collects all pages by following @odata.nextLink.
 *
 * @typeParam T - The typed business object (e.g., Shift, Attendance)
 */
export async function listItems<T>(
  client: Client,
  siteId: string,
  listId: string,
  options?: ListItemsOptions,
): Promise<ListItemsResult<T>> {
  const api = asApi(client);
  if (api) {
    const res = await api.post<{ items: T[] }>('/data/list-items', { listId, options });
    return { items: res.items };
  }

  const allItems: T[] = [];

  try {
    let request = client
      .api(`/sites/${siteId}/lists/${listId}/items`)
      .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
      .expand('fields');

    if (options?.filter) request = request.filter(options.filter);
    if (options?.orderby) request = request.orderby(options.orderby);
    if (options?.top) request = request.top(options.top);
    if (options?.select) {
      // Select on fields sub-properties
      const fieldSelects = options.select
        .map((f) => `fields/${f}`)
        .join(',');
      request = request.select(`id,${fieldSelects}`);
    }

    let response = (await request.get()) as {
      value: SharePointListItem[];
      '@odata.nextLink'?: string;
    };

    for (const item of response.value) {
      allItems.push({ id: item.id, ...item.fields } as T);
    }

    // Follow pagination links
    while (response['@odata.nextLink']) {
      response = (await client
        .api(response['@odata.nextLink'])
        .get()) as {
        value: SharePointListItem[];
        '@odata.nextLink'?: string;
      };

      for (const item of response.value) {
        allItems.push({ id: item.id, ...item.fields } as T);
      }
    }

    return { items: allItems };
  } catch (error) {
    if (error instanceof GraphApiError) throw error;
    handleGraphError(error, 'listItems');
  }
}

/**
 * Get a single list item by ID.
 */
export async function getItem<T>(
  client: Client,
  siteId: string,
  listId: string,
  itemId: string,
): Promise<T> {
  const api = asApi(client);
  if (api) {
    const res = await api.post<{ item: T }>('/data/get-item', { listId, itemId });
    return res.item;
  }

  try {
    const response = (await client
      .api(`/sites/${siteId}/lists/${listId}/items/${itemId}`)
      .expand('fields')
      .get()) as SharePointListItem;

    return { id: response.id, ...response.fields } as T;
  } catch (error) {
    if (error instanceof GraphApiError) throw error;
    handleGraphError(error, `getItem(${itemId})`);
  }
}

/**
 * Create a new list item.
 *
 * @param fields - Business fields to write (no SharePoint-internal fields)
 * @returns The created item with its SharePoint-assigned ID
 */
export async function createItem<T>(
  client: Client,
  siteId: string,
  listId: string,
  fields: Record<string, unknown>,
): Promise<T> {
  const api = asApi(client);
  if (api) {
    const res = await api.post<{ item: T }>('/data/create-item', { listId, fields });
    return res.item;
  }

  try {
    const response = (await client
      .api(`/sites/${siteId}/lists/${listId}/items`)
      .post({ fields })) as SharePointListItem;

    return { id: response.id, ...response.fields } as T;
  } catch (error) {
    if (error instanceof GraphApiError) throw error;
    handleGraphError(error, 'createItem');
  }
}

/**
 * Update an existing list item's fields.
 *
 * @param fields - Only the fields to update (partial update)
 */
export async function updateItem(
  client: Client,
  siteId: string,
  listId: string,
  itemId: string,
  fields: Record<string, unknown>,
): Promise<void> {
  const api = asApi(client);
  if (api) {
    await api.post('/data/update-item', { listId, itemId, fields });
    return;
  }

  try {
    await client
      .api(
        `/sites/${siteId}/lists/${listId}/items/${itemId}/fields`,
      )
      .patch(fields);
  } catch (error) {
    if (error instanceof GraphApiError) throw error;
    handleGraphError(error, `updateItem(${itemId})`);
  }
}

/**
 * Delete a list item.
 */
export async function deleteItem(
  client: Client,
  siteId: string,
  listId: string,
  itemId: string,
): Promise<void> {
  const api = asApi(client);
  if (api) {
    await api.post('/data/delete-item', { listId, itemId });
    return;
  }

  try {
    await client
      .api(`/sites/${siteId}/lists/${listId}/items/${itemId}`)
      .delete();
  } catch (error) {
    if (error instanceof GraphApiError) throw error;
    handleGraphError(error, `deleteItem(${itemId})`);
  }
}
